import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MfaForm } from "./login/MfaForm";
import { LoginForm } from "./login/LoginForm";
import Header from "./layout/Header";
import { getOriginatingPath } from "../../helpers/shared/location";
import { TotpSetupForm } from "./login/TotpSetupForm";
import { ActivateAgentForm } from "./login/ActivateAgentForm";
import A11yH1 from "../../components/basics/A11YH1";
import { useTranslation } from "react-i18next";
import TpaFooter from "../tpa/components/Footer";
import { SignInOutput } from "aws-amplify/auth";
import ConfirmAgentForm from "./login/ConfirmAgentForm";

export type OnLoginResponse = (response: any) => void;

enum Step {
  LOGIN = "LOGIN",
  NEW_PASSWORD = "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED",
  MFA_SETUP = "CONTINUE_SIGN_IN_WITH_TOTP_SETUP",
  SOFTWARE_TOKEN_MFA = "CONFIRM_SIGN_IN_WITH_TOTP_CODE",
  EMAIL_TOKEN_MFA = "CONFIRM_SIGN_IN_WITH_EMAIL_CODE",
  USER_DETAIL = "USER_DETAIL",
}

const switchChallenges = (challenge: string, setStep: any, setShowUserDetail: any, showUserDetail: boolean, onSuccess: any) => {
  switch (challenge) {
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      setStep(Step.NEW_PASSWORD);
      setShowUserDetail(true);
      break;
    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
      setStep(Step.MFA_SETUP);
      break;
    case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
      setStep(Step.SOFTWARE_TOKEN_MFA);
      break;
    case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
      setStep(Step.EMAIL_TOKEN_MFA);
      break;
    case "DONE":
      if (showUserDetail) {
        setStep(Step.USER_DETAIL);
        setShowUserDetail(false);
      } else {
        onSuccess();
      }
      break;
    default:
      console.error(`Unknown challenge ${challenge}`);
  }
};

export const Login = () => {
  const { t } = useTranslation("pages", { keyPrefix: "travel.header" });
  const [step, setStep] = useState<Step>(Step.LOGIN);
  const [cognitoUser, setCognitoUser] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const from = getOriginatingPath(location, "/");
  const resetLoginState = useCallback(() => setStep(Step.LOGIN), [setStep]);
  const [showUserDetail, setShowUserDetail] = useState(false);

  const onSuccess = () => {
    navigate(from, { replace: true });
  };

  const confirmUserDetail = () => {
    onLoginResponse({
      nextStep: {
        signInStep: "DONE",
      },
      isSignedIn: true,
    });
  };

  const onLoginResponse = (response: SignInOutput) => {
    const challenge = response.nextStep.signInStep;
    setCognitoUser(response);
    switchChallenges(challenge, setStep, setShowUserDetail, showUserDetail, onSuccess);
  };
  return (
    <main className="flex flex-col justify-start min-h-[inherit]">
      <A11yH1>{t("title")}</A11yH1>
      <Header home="/" className="gap-4" hideAccount />
      {step === Step.LOGIN && <LoginForm onLoginResponse={onLoginResponse} />}
      {step === Step.SOFTWARE_TOKEN_MFA && <MfaForm onLoginReset={resetLoginState} mfaType="SOFTWARE_TOKEN_MFA" onLoginResponse={onLoginResponse} />}
      {step === Step.EMAIL_TOKEN_MFA && <MfaForm onLoginReset={resetLoginState} mfaType="EMAIL_MFA" onLoginResponse={onLoginResponse} />}
      {step === Step.NEW_PASSWORD && <ActivateAgentForm onLoginResponse={onLoginResponse} />}
      {step === Step.USER_DETAIL && <ConfirmAgentForm onConfirm={confirmUserDetail} />}
      {step === Step.MFA_SETUP && <TotpSetupForm onLoginReset={resetLoginState} onSuccess={onSuccess} cognitoUser={cognitoUser as SignInOutput} />}
      <TpaFooter />
    </main>
  );
};
